import { useEffect, useState } from 'react';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import AdminSectionTitle from '../components/AdminSectionTitle';
import Summary from '../components/Summary';
import { formatCurrency } from '../utils/shared';
import BankAccountsTable from '../components/BankAccountsTable';
import useGetParams from '../hooks/useGetParams';
import { useGetEventBalanceQuery, useGetBankAccountsQuery, useGetWiresRequestsQuery } from '../store';
import WireTable from '../components/WireTable';
import useGetUserRole from '../hooks/useGetUserRole';
import TitleNumberCard from '../components/atoms/TitleNumberCard';
import useIsMobile from '../hooks/useIsMobile';

const BalancePage = () => {
  const { event } = useGetSelectedEvent();
  const { businessId, eventId } = useGetParams();
  const { data: balance } = useGetEventBalanceQuery({ businessId, eventId }, { skip: !businessId || !eventId });
  const [showWireModal, setShowWireModal] = useState(false);
  const { data: bankAccounts } = useGetBankAccountsQuery({ businessId });
  const { data: wiresRequests } = useGetWiresRequestsQuery({ businessId, eventId });
  const [maxToRequest, setMaxToRequest] = useState(0);
  const userRole = useGetUserRole();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (balance && wiresRequests) {
      const max = balance.total_balance - wiresRequests.filter((wire) => wire.status === 'pending').reduce((acc, wire) => acc + wire.amount, 0);
      setMaxToRequest(max);
    }
  }, [balance, wiresRequests]);

  const adminSummary = [
    {
      title: 'Total venta manual',
      value: formatCurrency(balance?.gross_received_manual_amount || 0, 'no-decimals'),
      description: 'Total vendido manual, despues de aplicar descuentos. No incluye comisiones.',
    },
    {
      title: 'Comisiones Avantti',
      value: formatCurrency(balance?.avantti_manual_fees || 0, 'no-decimals'),
      description: `Comisiones del ${(event?.manual_service_charge ?? 0) * 100}% aplicadas sobre la venta manual`,
    },
    {
      title: 'Recibido por Avantti',
      value: formatCurrency(balance?.avantti_gross_amount_online || 0, 'no-decimals'),
      description: 'Total recibido por Avantti en venta online, luego de aplicar descuentos.',
    },
    {
      title: 'Service Charge incluido',
      value: formatCurrency(balance?.absorbed_service_charge_amount || 0, 'no-decimals'),
      description: 'Total de Service Charge incluido en la venta manual.',
    },
    {
      title: 'Transferido',
      value: formatCurrency(balance?.total_transferred || 0, 'no-decimals'),
      description: 'Total transferido desde Avantti hacia cuentas de la productora.',
    },
    {
      title: 'Saldo',
      value: formatCurrency(balance?.total_balance || 0, 'no-decimals'),
      description: 'Total pendiente de transferir desde Avantti hacia cuentas de la productora.',
    },
  ];

  const rootSummary = [
    {
      title: 'Total dinero procesado',
      value: formatCurrency(balance?.total_processed || 0),
    },
    {
      title: 'Comisiones procesadores de pagos',
      value: formatCurrency(balance?.gateway_fees || 0),
    },
    {
      title: 'Service charge online',
      value: formatCurrency(balance?.avantti_online_fees || 0),
    },
    {
      title: 'Service charge manual',
      value: formatCurrency(balance?.avantti_manual_fees || 0),
    },
    {
      title: 'Revenue generado por el evento',
      value: formatCurrency(balance?.avantti_revenue || 0),
    },
  ];

  const handleClick = () => {
    setShowWireModal(true);
    setTimeout(() => {
      setShowWireModal(false);
    }, 1000);
  };
  const mainButtons = [
    {
      label: 'Solicitar dinero',
      image: '/coins-hand.svg',
      imageInverted: true,
      onClick: handleClick,
    },
  ];

  return (
    <div className="flex flex-col my-6 gap-8">
      <AdminSectionTitle title="Eventos" subtitle={`${event?.name} - Liquidaciones`} mainButtons={mainButtons} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start glass-surface">
        <div className="flex flex-col gap-4">
          <h1 className='text-xl font-semibold'>Balance del evento</h1>
          <Summary summary={adminSummary} />
        </div>
        {!isMobile && (
          <div className='flex flex-col gap-4'>
            <TitleNumberCard title="Dinero recibido por Avantti" number={formatCurrency(balance?.avantti_gross_amount_online || 0, 'no-decimals')} />
            <TitleNumberCard title="Comisiones Avantti" number={formatCurrency(((balance?.avantti_manual_fees ?? 0) + (balance?.absorbed_service_charge_amount ?? 0)) || 0, 'no-decimals')} />
            <TitleNumberCard title="Dinero transferido" number={formatCurrency(balance?.total_transferred || 0, 'no-decimals')} />
            <TitleNumberCard title="Dinero pendiente de transferir" number={formatCurrency(balance?.total_balance || 0, 'no-decimals')} />
          </div>
        )}
      </div>
      <div className='flex flex-col gap-4 glass-surface'>
        <h1 className='text-xl font-semibold'>Cuentas declaradas</h1>
        <BankAccountsTable data={bankAccounts || []} />
      </div>
      <div className='flex flex-col gap-4 glass-surface'>
        <h1 className='text-xl font-semibold'>Movimientos</h1>
        <WireTable
          data={wiresRequests || []}
          triggerModal={showWireModal}
          maxToRequest={maxToRequest}
        />
      </div>
      {userRole === 'ROOT' && <div className="flex flex-col gap-4 glass-surface">
        <h1 className='text-xl font-semibold'>Balance Superadmin</h1>
        <Summary summary={rootSummary} />
      </div>}
    </div>
  );
};

export default BalancePage;
